import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { Loader2, Eye, EyeOff, CheckCircle2, ShieldAlert } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-6 py-10">
        <button onClick={() => navigate("/")} className="mb-8 flex items-center gap-2" data-testid="reset-logo">
          <img src="/logo-mark.webp" alt="Send-Proof" className="h-9 w-9 rounded-lg" />
          <span className="font-heading text-xl font-bold tracking-tight">Send-Proof</span>
        </button>

        {!token ? (
          <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-5 text-center" data-testid="reset-no-token">
            <ShieldAlert className="mx-auto h-9 w-9 text-rose-500" />
            <p className="mt-3 text-sm font-semibold text-foreground">Invalid reset link</p>
            <p className="mt-1 text-sm text-muted-foreground">This link is missing its token. Request a new one from the sign-in screen.</p>
            <button onClick={() => navigate("/auth?mode=forgot")} data-testid="reset-request-new" className="mt-4 text-sm font-semibold text-emerald-600">Request a new link</button>
          </div>
        ) : done ? (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-center" data-testid="reset-done">
            <CheckCircle2 className="mx-auto h-9 w-9 text-emerald-500" />
            <p className="mt-3 text-sm font-semibold text-foreground">Password updated</p>
            <p className="mt-1 text-sm text-muted-foreground">You can now sign in with your new password.</p>
            <Button onClick={() => navigate("/auth?mode=signin")} data-testid="reset-go-signin" className="mt-4 h-11 w-full bg-emerald-500 text-base hover:bg-emerald-600">Sign in</Button>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-bold">Choose a new password</h1>
            <p className="mt-1 text-sm text-muted-foreground">Enter a new password for your account.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="new-password">New password</Label>
                <div className="relative mt-1">
                  <Input
                    id="new-password"
                    type={showPw ? "text" : "password"}
                    required
                    data-testid="reset-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="h-11 pr-11"
                  />
                  <button
                    type="button"
                    data-testid="reset-password-toggle"
                    aria-label={showPw ? "Hide password" : "Show password"}
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center text-muted-foreground transition-transform active:scale-90 hover:text-foreground"
                  >
                    {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={busy} data-testid="reset-submit-btn" className="h-11 w-full bg-emerald-500 text-base hover:bg-emerald-600">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
